import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as KeplrIntegration from '../wallets/KeplrIntegration';
import * as LeapIntegration from '../wallets/LeapIntegration';
import * as MetaMaskIntegration from '../wallets/MetaMaskIntegration';
import * as MagicEdenIntegration from '../wallets/MagicEdenIntegration';
import * as SolflareIntegration from '../wallets/SolflareIntegration';
import { toggleButtonsVisibility } from '../ext/helpers';
import { showMessage, endSession, isWalletAvailable } from '../wallets/walletUtils';
import { BADART_URL } from '../config';

const Header = ({ 
  ownershipProved, 
  starsAddress, 
  setStarsAddress, 
  setOwnershipProved, 
  setMessage, 
  setMessageType,
  setTotalCount,
  showWalletModal,
  setShowWalletModal
}) => {
  const navigate = useNavigate();
  const activeWallet = sessionStorage.getItem('activeWallet');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [walletAvailability, setWalletAvailability] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    if (showWalletModal) {
      const availability = {
        Keplr: isWalletAvailable('Keplr'),
        Leap: isWalletAvailable('Leap'),
        MetaMask: isWalletAvailable('MetaMask'),
        'Magic Eden': isWalletAvailable('MagicEden'),
        Solflare: isWalletAvailable('Solflare')
      };
      setWalletAvailability(availability);
    }
  }, [showWalletModal]);

  const getActiveWalletIntegration = () => {
    return activeWallet === 'Leap' ? LeapIntegration : KeplrIntegration;
  };

  const handleEndSession = () => {
    sessionStorage.removeItem('activeWallet');
    endSession(
      setStarsAddress, 
      setOwnershipProved, 
      null,
      (msg, type) => showMessage(setMessage, setMessageType, msg, type),
      navigate
    );
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode);
    window.dispatchEvent(new CustomEvent('darkModeChange', { detail: newDarkMode }));
  };

  const validateEmail = (email) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsEmailValid(validateEmail(newEmail));
  };

  const handleSendVerificationCode = async () => {
    try {
      const response = await fetch(`${BADART_URL}/send-verification.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setShowVerificationInput(true);
        setIsResendDisabled(true);
        setResendCountdown(30);
        setVerificationCode(''); // Clear verification code input when resending
        
        const timer = setInterval(() => {
          setResendCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              setIsResendDisabled(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        showMessage(setMessage, setMessageType, "Verification code sent!", "success");
      } else {
        showMessage(setMessage, setMessageType, "Failed to send verification code", "error");
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      showMessage(setMessage, setMessageType, "Error sending verification code", "error");
    }
  };

  const handleVerifyCode = async () => {
    try {
      const response = await fetch(`${BADART_URL}/verify-code.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code: verificationCode })
      });

      if (response.ok) {
        const { address } = await response.json();
        setStarsAddress(address);
        setOwnershipProved(true);
        sessionStorage.setItem('starsAddress', address);
        sessionStorage.setItem('ownershipProved', 'true');
        sessionStorage.setItem('activeWallet', 'email');
        sessionStorage.setItem('userEmail', email); // Store email in session
        setShowEmailModal(false);
        showMessage(setMessage, setMessageType, "Email verified successfully!", "success");
      } else {
        showMessage(setMessage, setMessageType, "Invalid verification code", "error");
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      showMessage(setMessage, setMessageType, "Error verifying code", "error");
    }
  };

  const walletModalContent = (
    <div className="wallet-modal-body">
      {[
        {
          name: 'Keplr',
          icon: 'img/keplr-icon.png',
          integration: KeplrIntegration
        },
        {
          name: 'Leap',
          icon: 'img/leap-icon.png',
          integration: LeapIntegration
        },
        {
          name: 'MetaMask',
          icon: 'img/metamask-icon.png',
          integration: MetaMaskIntegration
        },
        {
          name: 'Magic Eden',
          icon: 'img/magiceden-icon.png',
          integration: MagicEdenIntegration
        },
        {
          name: 'Solflare',
          icon: 'img/solflare-icon.jpeg',
          integration: SolflareIntegration
        },
        {
          name: 'Email Login',
          icon: 'img/email-icon.png',
          isEmailLogin: true // Add this flag
        }
      ].map((wallet, index) => (
        <button 
          key={wallet.name}
          className={`wallet-option ${!wallet.isEmailLogin && !walletAvailability[wallet.name] ? 'wallet-unavailable' : ''}`}
          onClick={() => {
            if (wallet.isEmailLogin) {
              setShowWalletModal(false);
              setShowEmailModal(true);
            } else if (walletAvailability[wallet.name]) {
              wallet.integration.proveAccountOwnership(
                setStarsAddress, 
                setOwnershipProved,
                (msg, type) => showMessage(setMessage, setMessageType, msg, type)
              );
              setShowWalletModal(false);
            }
          }}
        >
          <img src={wallet.icon} alt={wallet.name} />
          <span className='text-dark'>{wallet.name}</span>
          <span className="wallet-status ms-auto">
            {!wallet.isEmailLogin && walletAvailability[wallet.name] ? (
              <span className='text-success fs-6'><i className="bi bi-check-circle-fill text-success me-2"></i>installed</span>
            ) : (
              <></>
            )}
          </span>
        </button>
      ))}
    </div>
  );

  const emailModalContent = (
    <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
      <div className="wallet-modal email-modal" onClick={e => e.stopPropagation()}>
        <div className="wallet-modal-header">
          <h5 className="modal-title">Email Verification</h5>
          <button className="modal-close-btn" onClick={() => setShowEmailModal(false)}>
            <i className="bi bi-x"></i>
          </button>
        </div>
        <div className="wallet-modal-body">
          <div className="email-verification-form">
            <div className="form-group">
              <input
                type="email"
                className={`form-control ${email && !isEmailValid ? 'is-invalid' : ''}`}
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                disabled={showVerificationInput}
              />
              {email && !isEmailValid && (
                <div className="invalid-feedback">Please enter a valid email address</div>
              )}
            </div>
            
            {!showVerificationInput && (
              <button
                className="btn btn-dark w-100 mt-3"
                disabled={!isEmailValid}
                onClick={handleSendVerificationCode}
              >
                Continue
              </button>
            )}

            {showVerificationInput && (
              <>
                <div className="form-group mt-3">
                  <input
                    type="text"
                    className="form-control verification-input"
                    placeholder="Enter verification code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-dark w-100 mt-3"
                  disabled={verificationCode.length !== 6}
                  onClick={handleVerifyCode}
                >
                  Verify
                </button>
                <button
                  className="btn btn-link w-100"
                  disabled={isResendDisabled}
                  onClick={handleSendVerificationCode}
                >
                  {isResendDisabled 
                    ? `Resend code in ${resendCountdown}s` 
                    : 'Resend code'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="row mb-3">
        <div className="col-8 col-md-5 rounded-top ms-1 pt-3">
          <h2 className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}><span className='text-secondary'>bad</span>art <sup>APP</sup></h2>
        </div>
        
        <div className="col p-0">

          <div className="bg-light float-end pb-2">
            
            {ownershipProved && (
              <div className="d-inline ms-1 font-small">
                <small className='me-2 ms-3'>
                  {starsAddress ? 
                    (sessionStorage.getItem('activeWallet') === 'email' ? 
                      sessionStorage.getItem('userEmail') : 
                      getActiveWalletIntegration().shortenAddress(starsAddress)) : 
                    'Not connected'}
                </small>
                <button className='btn p-0' onClick={() => getActiveWalletIntegration().copyToClipboard(starsAddress)}>
                  <i className="bi bi-copy text-dark me-3" id='copy_add'></i>
                </button>
              </div>
            )}

            {!ownershipProved ? (
              <>
                <button
                  className="btn bg-dark ms-2 loginBtnNot text-white d-inline"
                  onClick={() => setShowWalletModal(true)}>
                  Connect
                </button>

                {showWalletModal && (
                  <div className="modal-overlay" onClick={() => setShowWalletModal(false)}>
                    <div className="wallet-modal" onClick={e => e.stopPropagation()}>
                      <div className="wallet-modal-header">
                        <p className='d-block ms-3 mb-0 mt-2 h5 py-3'>Connect your wallet</p>
                        <button className="modal-close-btn" onClick={() => setShowWalletModal(false)}>
                          <i className="bi bi-x text-dark"></i>
                        </button>
                      </div>
                      {walletModalContent}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="btn-group">
                <button 
                  className="btn btn-secondary logoutBtn text-white d-inline" 
                  onClick={handleEndSession}>
                  <i className="bi bi-box-arrow-right"></i>
                </button>
              </div>
            )}
          </div>
          <button
              className="theme-toggle nav-link d-inline me-2 float-end"
              onClick={toggleDarkMode}
              aria-label="Toggle dark mode"
            >
              {darkMode ? (
                <i className="bi bi-sun-fill"></i>
              ) : (
                <i className="bi bi-moon-fill"></i>
              )}
            </button>
          {ownershipProved && (
          <a href='#/tickets' className='float-end pt-2'>
            <i className="fs-5 bi bi-bell-fill text-dark"></i>
          </a>
          )}
        </div>
        <div className="clearfix"></div>
      </div>
      {showEmailModal && emailModalContent}
    </>
  );
};

export default Header;